import { PollyEnv } from '../types';

/**
 * Scan all cookies for the Cognito token.
 * Prefers the Access token — required by /auth/users/me and other Polly API
 * endpoints (matches Atlas Manager behaviour). Falls back to ID token if the
 * access token is not found.
 */
export function getCognitoBearerToken(): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  let idToken: string | null = null;
  let accessToken: string | null = null;
  for (const c of cookies) {
    const eq = c.indexOf('=');
    if (eq === -1) continue;
    const name = c.slice(0, eq).trim();
    const value = c.slice(eq + 1).trim();
    if (!value) continue;
    if (name.startsWith('CognitoIdentityServiceProvider.') && name.endsWith('.accessToken')) {
      accessToken = decodeURIComponent(value);
    } else if (name.startsWith('CognitoIdentityServiceProvider.') && name.endsWith('.idToken')) {
      idToken = decodeURIComponent(value);
    }
  }
  return accessToken ?? idToken;
}

/** Scan all cookies for the Cognito refresh token. */
export function getCognitoRefreshToken(): string | null {
  if (typeof document === 'undefined') return null;
  for (const c of document.cookie.split(';')) {
    const eq = c.indexOf('=');
    if (eq === -1) continue;
    const name = c.slice(0, eq).trim();
    const value = c.slice(eq + 1).trim();
    if (
      name.startsWith('CognitoIdentityServiceProvider.') &&
      name.endsWith('.refreshToken')
    ) {
      return value ? decodeURIComponent(value) : null;
    }
  }
  return null;
}

/**
 * Decode a JWT payload without verifying the signature.
 * Safe for client-side expiry checks — we only read the `exp` claim.
 * Signature verification happens server-side at the API gateway.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // Base64url → base64 → JSON
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '='));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Returns true if the token is expired (or unparseable).
 * Uses the JWT `exp` claim (Unix seconds) with a 30-second clock-skew buffer.
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return payload.exp < Math.floor(Date.now() / 1000) + 30;
}

/** Infer Polly environment from the current hostname. */
export function detectEnvFromHostname(): PollyEnv {
  const h = window.location.hostname;
  if (h.includes('devpolly')) return 'devpolly';
  if (h.includes('testpolly')) return 'testpolly';
  return 'polly';
}

/** Returns true when running on localhost / 127.0.0.1. */
export function isLocalhost(): boolean {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

const STORED_ENV_KEY = 'kg-manager-env';

/**
 * Resolve the Polly env to use for token-based auth.
 * On deployed envs: always use hostname.
 * On localhost: use stored user preference, defaulting to 'testpolly'.
 */
export function resolveEnvForToken(): PollyEnv {
  if (!isLocalhost()) return detectEnvFromHostname();
  const stored = localStorage.getItem(STORED_ENV_KEY);
  if (stored === 'polly' || stored === 'testpolly' || stored === 'devpolly') return stored;
  return 'testpolly'; // safe default for local dev
}

/** Persist the user's environment selection (used by the login toggle). */
export function saveSelectedEnv(env: PollyEnv): void {
  localStorage.setItem(STORED_ENV_KEY, env);
}

/** Read back the persisted env selection. */
export function loadSelectedEnv(): PollyEnv {
  const stored = localStorage.getItem(STORED_ENV_KEY);
  if (stored === 'polly' || stored === 'testpolly' || stored === 'devpolly') return stored;
  return 'testpolly';
}

/** Build the Polly prelogin URL that redirects back to the given return URL. */
export function getPollyLoginUrl(env: PollyEnv, returnUrl: string): string {
  const base =
    env === 'devpolly'
      ? 'https://devpolly.elucidata.io'
      : env === 'testpolly'
      ? 'https://testpolly.elucidata.io'
      : 'https://polly.elucidata.io';
  return `${base}/prelogin/?redirect_url=${encodeURIComponent(returnUrl)}`;
}

/** Build the Polly logout URL that redirects back to the given return URL. */
export function getPollyLogoutUrl(env: PollyEnv, returnUrl: string): string {
  const base =
    env === 'devpolly'
      ? 'https://devpolly.elucidata.io'
      : env === 'testpolly'
      ? 'https://testpolly.elucidata.io'
      : 'https://polly.elucidata.io';
  return `${base}/logout?redirect_url=${encodeURIComponent(returnUrl)}`;
}

/**
 * Get the Polly platform base URL for the current hostname.
 * On localhost, falls back to the stored env preference so that the
 * session-expired modal redirects to the same env the user logged in with.
 */
export function getPollyPlatformUrl(): string {
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.includes('devpolly')) return 'https://devpolly.elucidata.io';
  if (h.includes('testpolly')) return 'https://testpolly.elucidata.io';
  if (h === 'localhost' || h === '127.0.0.1') {
    const stored = localStorage.getItem(STORED_ENV_KEY);
    if (stored === 'testpolly') return 'https://testpolly.elucidata.io';
    if (stored === 'devpolly') return 'https://devpolly.elucidata.io';
    return 'https://testpolly.elucidata.io'; // safe default for local dev
  }
  return 'https://polly.elucidata.io';
}

/**
 * Delete all Cognito SSO cookies from the current env domain.
 * Must be called before redirecting to /prelogin/ so Polly doesn't
 * see a live session and skip straight to Atlas.
 * Matches Atlas Manager's clearTokens() in cookies.ts.
 */
export function clearCognitoTokens(): void {
  if (typeof document === 'undefined') return;
  const env = resolveEnvForToken();
  const domain =
    env === 'devpolly'  ? '.devpolly.elucidata.io'  :
    env === 'testpolly' ? '.testpolly.elucidata.io' :
                          '.polly.elucidata.io';

  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const eq = c.indexOf('=');
    if (eq === -1) continue;
    const name = c.slice(0, eq).trim();
    if (!name.includes('CognitoIdentityServiceProvider')) continue;
    // Delete with domain (deployed envs)
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${domain};`;
    // Also delete without domain (localhost fallback)
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;`;
  }
}

/** Redirect the browser to the Polly login page for the given environment. */
export function redirectToPollyLogin(env: PollyEnv): void {
  // Use origin (not href) so Polly redirects back to the root after login,
  // ensuring the app re-initializes cleanly with a fresh session.
  window.location.href = getPollyLoginUrl(env, window.location.origin);
}
